import { CARD_ELEMENT_TYPE, CARD_SIDE, ORIENTATION } from '../../constants';
import { IMAGE_SIZE_CODE } from '../../../../constants/image-size-codes';
import { getMaxThumb, getThumbBySize } from '../../../../util/photo';
import { isCardElementEditable, isFullFrame } from '../AccountCard/helpers';
import { getFitPositionPhotoCropbox } from '../../../prints/components/PrintProductConfigurator/helpers';
import { IMAGE_BLUR_TYPES } from '../../../../constants/image-blur-types';
import { PHOTO_TYPES } from '../../../../constants/photo-types';

/**
 * Gets a CardElement's overlay CardElement (in case of an Overlay photo CardElement).
 * Note: This is a very naive method that simply brings the first Photo CardElement with a valid
 * configuration and a valid asset image that is also non-editable.
 *
 * @param {string} cardElementId The CardElement id that we want the overlay to be applied on.
 * @param {Array<Object>} elements The CardElement's state for the particular CardSide.
 * @returns {Object|undefined}
 */
export const getCardElementPhotoOverlayElement = (cardElementId, elements) => {
  const editablePhotoElements = elements.filter(
    element =>
      element.cardElement.type === CARD_ELEMENT_TYPE.PHOTO &&
      element.side === CARD_SIDE.FRONT &&
      isCardElementEditable(element),
  );

  // Only apply an overlay element if it's a single customizable photo template.
  // Currently we do not support overlaying the card's asset on multi-photo templates.
  if (editablePhotoElements?.length > 1) {
    return undefined;
  }

  return elements.find(element => {
    const { cardElement, cardElementConfiguration } = element;
    const isEditable = isCardElementEditable(element);

    return (
      element.side === CARD_SIDE.FRONT &&
      cardElement.type === CARD_ELEMENT_TYPE.PHOTO &&
      cardElement.id !== cardElementId &&
      !!cardElementConfiguration.asset_image &&
      isFullFrame(cardElement.frame) &&
      //  Backgrounds meet full frame but have a base z_index of 1.
      //  this is intended to skip those, as technically no overlay
      //  should have a z_index of 1.
      cardElement.z_index > 1 &&
      cardElementConfiguration?.photo_type !== PHOTO_TYPES.FOREGROUND &&
      !isEditable
    );
  });
};

/**
 * Initial photo cropbox factory.
 *
 * @returns {{rotation: number, x: number, y: number, scale: number}}
 */
export const getInitialPhotoCropbox = () => ({
  x: 0,
  y: 0,
  scale: 1,
  rotation: 0,
});

/**
 * Returns the URL of a selected AssetImage or Photo from a
 * photo card element configuration.
 *
 * @param {Object} cardElementConfiguration The CardElement's CardElementConfiguration.
 * @param {IMAGE_SIZE_CODE} imageSizeCode The image size code.
 * @param {Boolean} shouldUseFaceblockedUrls whether or not to use faceblocked urls
 * @returns {undefined|*}
 */
export const getCardPhotoElementConfigurationImageUrl = (
  cardElementConfiguration,
  imageSizeCode = IMAGE_SIZE_CODE.LARGE,
  shouldUseFaceblockedUrls = false,
) => {
  if (!cardElementConfiguration) {
    return undefined;
  }

  const {
    photo_url: photoUrl,
    asset_image: assetImage,
    photo,
  } = cardElementConfiguration;

  if (photoUrl) {
    return photoUrl;
  }

  if (photo) {
    if (
      shouldUseFaceblockedUrls &&
      photo?.foreground?.watermarked_original_url?.url
    ) {
      return photo.foreground.watermarked_original_url?.url;
    }

    if (photo?.foreground?.original_url?.url) {
      return photo?.foreground?.original_url?.url;
    }

    if (
      shouldUseFaceblockedUrls &&
      photo.matched_photos?.records?.[0]?.faceblocked_thumbnail_urls?.length > 0
    ) {
      return photo.matched_photos?.records?.[0]?.faceblocked_thumbnail_urls?.filter(
        thumb => thumb.blur_type === IMAGE_BLUR_TYPES.FOREGROUND,
      )?.[0]?.url;
    }

    if (
      shouldUseFaceblockedUrls &&
      photo.faceblocked_thumbnail_urls?.length > 0
    ) {
      return photo.faceblocked_thumbnail_urls?.filter(
        thumb => thumb.blur_type === IMAGE_BLUR_TYPES.FOREGROUND,
      )?.[0]?.url;
    }

    return getThumbBySize(photo.thumbnail_urls, imageSizeCode, true)?.url;
  }

  return assetImage?.url;
};

export const cardElementPhotoConfigurationHasMinPPI = ({
  element,
  cardWidth,
  cardHeight,
  photo,
}) => {
  if (!element || !cardWidth || !cardHeight || !photo?.thumbnail_urls) {
    return true;
  }

  const MIN_PPI = 120;
  // Pixels
  const { width: photoWidth, height: photoHeight } = getMaxThumb(
    photo.thumbnail_urls,
  );
  // Percentage (0-1) of cardWidth, cardHeight
  const {
    width: cardElementWidth,
    height: cardElementHeight,
  } = element.cardElement.frame;

  if (cardWidth * cardHeight > 200) {
    // We avoid running this check on really large print areas
    // as we run the risk of having a lot of false negatives. Larger prints
    // are meant to be seen from a larger distance so a lower DPI is fine in most cases.
    // TODO revisit this with more data once it runs a bit in production.
    return true;
  }

  const { scale = 1 } = element.cardElementConfiguration?.cropbox ?? {};

  // https://www.calculatorsoup.com/calculators/technology/ppi-calculator.php
  const cardElementWidthInches = cardWidth * cardElementWidth;
  const cardElementHeightInches = cardHeight * cardElementHeight;
  const cardElementDiagonalInches = Math.sqrt(
    cardElementWidthInches ** 2 + cardElementHeightInches ** 2,
  );
  const photoDiagonalPixels = Math.sqrt(photoWidth ** 2 + photoHeight ** 2);
  // Calculate PPI and offset by the scale (zoom) of the photo.
  const ppi = photoDiagonalPixels / cardElementDiagonalInches / scale;

  return ppi >= MIN_PPI;
};

/**
 * Takes in the element and general details to simplify calculating a
 * fitted cropbox for a photo added to a container
 *
 * @param {Object} opts List of options
 * @param {Object} opts.cardElement The card element
 * @param {Number} opts.cardWidth the card width
 * @param {Number} opts.cardHeight the card height
 * @param {Object} opts.thumb a thumbnail of the photo, including width & height
 * @param {Array} opts.faceboxes a set of faces belonging to the photo
 * @returns {{rotation: number, x: number, y: number, scale: number}}
 */
export const getAutoFitPositionPhotoCropbox = ({
  cardElement = {},
  cardWidth,
  cardHeight,
  thumb,
  faceboxes,
}) => {
  const { frame } = cardElement;
  // Parse the frame. Since we could be inside a custom print we
  // need to account for the possibility where the "proportions"
  // of the frame are not enough. .4 W and .5 H does *not* mean
  // portrait if our card is, for example, 2 W and 1 H, as the
  // real frame sizing would be .4 * 2 (.8W) and .5 * 1 (.5H).
  const parsedFrame = frame
    ? {
        width: Math.round(frame.width * cardWidth * 1e2) / 1e2,
        height: Math.round(frame.height * cardHeight * 1e2) / 1e2,
      }
    : undefined;

  return getFitPositionPhotoCropbox(
    parsedFrame,
    parsedFrame?.height > parsedFrame?.width
      ? ORIENTATION.PORTRAIT
      : ORIENTATION.LANDSCAPE,
    thumb,
    faceboxes,
  );
};
